// axios.js

import axios from "axios";

import {
  getAccessToken,
  saveAccessToken,
  getRefreshToken,
  saveRefreshToken,
  logout,
} from "../utils/auth.js";

/* ========================================
   AXIOS INSTANCE
======================================== */

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,

  withCredentials: true,

  timeout: 60000,
});

/* ========================================
   REQUEST INTERCEPTOR
======================================== */

api.interceptors.request.use(
  (config) => {

    const token = getAccessToken();

    if (token) {

      config.headers = config.headers || {};

      config.headers.Authorization =
        `Bearer ${token}`;
    }

    return config;
  },

  (error) => Promise.reject(error)
);

/* ========================================
   REFRESH STATE
======================================== */

let isRefreshing = false;

let refreshSubscribers = [];

/* ========================================
   SUBSCRIBE PENDING REQUESTS
======================================== */

const subscribeTokenRefresh = (callback) => {

  refreshSubscribers.push(callback);
};

/* ========================================
   RELEASE PENDING REQUESTS
======================================== */

const onRefreshed = (newToken) => {

  refreshSubscribers.forEach(
    (callback) => callback(newToken)
  );

  refreshSubscribers = [];
};

/* ========================================
   CLEAR SUBSCRIBERS
======================================== */

const clearSubscribers = () => {

  refreshSubscribers = [];
};

/* ========================================
   RESPONSE INTERCEPTOR
======================================== */

api.interceptors.response.use(

  (response) => response,

  async (error) => {

    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const responseData =
      error.response?.data || {};

    const errorCode =
      responseData.code ||
      responseData.data?.code;

    const statusCode =
      error.response?.status;

    // Prevent infinite loop
    const isRefreshRequest =
      originalRequest.url?.includes(
        "/auth/refresh-token"
      );

    /* ========================================
       NETWORK ERRORS
       NEVER LOGOUT
    ======================================== */

    if (
      !error.response ||
      error.code === "ECONNABORTED"
    ) {

      console.warn(
        "🌐 Network/server issue — not logging out"
      );

      return Promise.reject(error);
    }

    /* ========================================
       REFRESH ALREADY RUNNING
       WAIT + RETRY
    ======================================== */

    if (
      errorCode === "REFRESH_IN_PROGRESS"
    ) {

      console.warn(
        "⏳ Refresh already in progress"
      );

      await new Promise(
        (resolve) =>
          setTimeout(resolve, 1000)
      );

      return api(originalRequest);
    }

    /* ========================================
       PERMANENT SESSION FAILURE
       ONLY HERE LOGOUT
    ======================================== */

    if (
      errorCode === "SESSION_INVALIDATED" ||
      errorCode === "INVALID_REFRESH_TOKEN" ||
      errorCode === "REFRESH_TOKEN_EXPIRED" ||
      errorCode === "REFRESH_TOKEN_REUSED"
    ) {

      console.warn(
        "🚪 Permanent session failure"
      );

      logout();

      if (
        window.location.pathname !== "/login"
      ) {

        window.location.href = "/login";
      }

      return Promise.reject(error);
    }

    /* ========================================
       ONLY REFRESH ON TOKEN_EXPIRED
    ======================================== */

    const shouldRefresh =
      statusCode === 401 &&
      errorCode === "TOKEN_EXPIRED";

    if (
      shouldRefresh &&
      !originalRequest._retry &&
      !isRefreshRequest
    ) {

      originalRequest._retry = true;

      /* ====================================
         IF REFRESH RUNNING
         QUEUE REQUESTS
      ==================================== */

      if (isRefreshing) {

        return new Promise(
          (resolve, reject) => {

            subscribeTokenRefresh(
              (newToken) => {

                try {

                  originalRequest.headers =
                    originalRequest.headers || {};

                  originalRequest.headers.Authorization =
                    `Bearer ${newToken}`;

                  resolve(
                    api(originalRequest)
                  );

                } catch (err) {

                  reject(err);
                }
              }
            );
          }
        );
      }

      isRefreshing = true;

      try {

        console.log(
          "🔄 Refreshing access token"
        );

        /* ====================================
           REFRESH REQUEST
        ==================================== */

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/auth/refresh-token`,
          {
            refreshToken:
              getRefreshToken(),
          },
          {
            withCredentials: true,
          }
        );

        const newAccessToken =
          response.data.accessToken;

        const newRefreshToken =
          response.data.refreshToken;

        /* ====================================
           VALIDATE TOKENS
        ==================================== */

        if (
          !newAccessToken ||
          !newRefreshToken
        ) {

          throw new Error(
            "Tokens not received"
          );
        }

        /* ====================================
           SAVE TOKENS
        ==================================== */

        saveAccessToken(
          newAccessToken
        );

        saveRefreshToken(
          newRefreshToken
        );

        /* ====================================
           RELEASE QUEUED REQUESTS
        ==================================== */

        onRefreshed(
          newAccessToken
        );

        isRefreshing = false;

        /* ====================================
           RETRY ORIGINAL REQUEST
        ==================================== */

        originalRequest.headers =
          originalRequest.headers || {};

        originalRequest.headers.Authorization =
          `Bearer ${newAccessToken}`;

        return api(originalRequest);

      } catch (refreshError) {

        isRefreshing = false;

        clearSubscribers();

        const refreshCode =
          refreshError.response?.data?.code;

        /* ====================================
           ONLY LOGOUT ON PERMANENT FAILURE
        ==================================== */

        if (
          refreshCode ===
            "INVALID_REFRESH_TOKEN" ||

          refreshCode ===
            "REFRESH_TOKEN_EXPIRED" ||

          refreshCode ===
            "SESSION_INVALIDATED" ||

          refreshCode ===
            "REFRESH_TOKEN_REUSED"
        ) {

          logout();

          if (
            window.location.pathname !==
            "/login"
          ) {

            window.location.href =
              "/login";
          }
        }

        return Promise.reject(
          refreshError
        );
      }
    }

    return Promise.reject(error);
  }
);

export default api;